<template>
  <div
    id="layout"
    class="antialiased bg-white text-stone-900 font-sans border-b flex flex-col min-h-screen"
  >
    <client-only>
      <Sidebars />
    </client-only>

    <ErrorBoundary>
      <client-only>
        <Notifications />
      </client-only>
    </ErrorBoundary>

    <header>
      <PartnerNavbar
        :logo-img="partnerDataComputed?.logo"
        :extra-link-items="[
          {
            text: 'general.supplierProfileButton',
            path: `/p/${partnerDataComputed?.permalink}/`,
            showOnSub: true,
          },
        ]"
      />
      <slot name="banner" />
    </header>

    <div class="flex-1">
      <main>
        <slot />
      </main>
    </div>
    <slot name="footer">
      <ErrorBoundary>
        <Footer
          :footer-block-items="footerBlocks"
          :display-newsletter-subscription-form="false"
        />
      </ErrorBoundary>
    </slot>
  </div>
</template>

<script lang="ts" setup>
const subdom = useSubdomain()
const { locale, t: $t, baseUrl } = useI18n()
const $localePath = useLocalePath()
const localePath = $localePath
const { data: partnerData } = await useSubdomainData(subdom.value)
const contactInfo = computed(
  () => partnerData.value?.booking_solution_settings?.data?.contact_info
)
const { setupValidationPolling, deactivateValidationPolling } =
  useCartValidation()

const { data: footerData } = useStrapiSingleType(`booking-solution-footer`, {
  query: { locale, 'populate[footerBlock][populate]': '*' },
})
const partnerDataComputed = computed(() => {
  /* if partner has no logo default domain will be shown */
  return {
    logo: supplierGetters.getHeaderLogo(partnerData.value) || undefined,
    permalink: supplierGetters.getPermalink(partnerData.value),
  }
})

const footerBlocks = computed(() => {
  const { footerBlock = [] } = footerData?.value?.data || {}
  if (contactInfo.value && partnerData.value) {
    const phone = contactInfo.value?.phone
    const email = contactInfo.value?.email
    const address = contactInfo.value?.address
    const website = partnerData.value?.website

    if (footerBlock[0]) {
      footerBlock[0].items = [
        ...(website ? [{ id: 3, label: website, link: website }] : []),
        ...(address ? [{ id: 2, label: address }] : []),
        ...(phone ? [{ id: 0, label: phone, link: `tel:${phone}` }] : []),
        ...(email ? [{ id: 1, label: email, link: `mailto:${email}` }] : []),
      ]
    }
  }
  if (footerBlock[2]) {
    footerBlock[2].items = [
      {
        label: $t('general.titles.home') + $t('general.titles.base'),
        imgUrl: '/_nuxt3-static/logos/full-logo-color-white.svg',
        link: partnerData.value
          ? baseUrl.value + localePath(`/p/${partnerData.value.permalink}`)
          : localePath('/'),
      },
    ]
  }
  return [
    ...footerBlock,
    {
      id: 141,
      title: $t('general.paymentMethods'),
      items: [
        {
          id: 1355,
          label: 'Mastercard',
          link: null,
          imgUrl: 'https://gokonfetti.com/_nuxt3-static/logos/mastercard.svg',
        },
        {
          id: 1356,
          label: 'Visa',
          link: null,
          imgUrl: 'https://gokonfetti.com/_nuxt3-static/logos/visa.svg',
        },
        {
          id: 1357,
          label: 'Paypal',
          link: null,
          imgUrl: 'https://gokonfetti.com/_nuxt3-static/logos/paypal.svg',
        },
        {
          id: 1358,
          label: 'Apple Pay',
          link: null,
          imgUrl: 'https://gokonfetti.com/_nuxt3-static/logos/apple-pay.svg',
        },
        {
          id: 1359,
          label: 'Google Pay',
          link: null,
          imgUrl: 'https://gokonfetti.com/_nuxt3-static/logos/google-pay.svg',
        },
      ],
    },
    {
      id: 24,
      title: $t('general.followUs'),
      items: [
        {
          id: 232,
          label: 'facebook',
          link: 'https://www.facebook.com/gokonfetti',
          imgUrl: 'https://gokonfetti.com/icons/facebook-light.svg',
        },
        {
          id: 233,
          label: 'LinkedIn',
          link: 'https://www.linkedin.com/company/72680824',
          imgUrl: 'https://gokonfetti.com/icons/linkedin-light.svg',
        },
        {
          id: 234,
          label: 'Instagram',
          link: 'https://www.instagram.com/gokonfetti/',
          imgUrl: 'https://gokonfetti.com/icons/instagram-light.svg',
        },
      ],
    },
  ]
})

onMounted(async () => {
  await setupValidationPolling()
})

onUnmounted(async () => {
  await deactivateValidationPolling()
})

useLayoutHead()
</script>
<style lang="postcss" scoped>
header {
  @apply sticky top-0 z-20;
}

#layout {
  box-sizing: border-box;
}
</style>

<style>
.Toastify__toast-container {
  max-width: 480px;
  --toastify-toast-width: max-content;
}
</style>
